import React, { useRef, useState, useEffect, useCallback } from 'react';
import { saveAs } from 'file-saver';
import PropTypes from 'prop-types';
import useUserStore from '../store/userStore';
import usePNGHandler from './usePNGHandler';

/**
 * Кнопка "Download PNG" с функцией копирования
 *
 * @param {Object[]} assets
 * @param {string} archiveName
 * @param {boolean} is_purchased
 * @param {boolean} isSubscriptionActive
 * @param {function} onBeforeAction    // если вернёт (или зарезолвит) false, не скачиваем
 * @param {function} onActionComplete  // вызываем после успешного скачивания
 * @param {boolean} debug
 */
const DownloadPNGButton = ({
  assets,
  debug = false,
  archiveName,
  is_purchased,
  isSubscriptionActive,
  onBeforeAction,
  onActionComplete,
}) => {
  if (debug) {
    console.log('[DownloadPNGButton] props:', {
      assets,
      debug,
      archiveName,
      is_purchased,
      isSubscriptionActive
    });
  }

  const accessToken = useUserStore((state) => state.accessToken);
  const isLoggedIn = useUserStore((state) => state.isLoggedIn);

  const {
    isDownloading,
    setIsDownloading,
    errorMessage,
    setErrorMessage,
    dropdownSizes,
    freeSizes,
    defaultSize,
    handleDownloadLogic,
    saveSelectedSize,
    collectionName
  } = usePNGHandler(assets, accessToken, isLoggedIn, is_purchased, isSubscriptionActive, debug, archiveName);

  const [showDropdown, setShowDropdown] = useState(false);
  const [arrowHover, setArrowHover] = useState(false);
  const [hoveredSize, setHoveredSize] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [copyErrorMessage, setCopyErrorMessage] = useState('');

  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Автоматически скрываем «Copied!» через 1 секунду
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  /**
   * Проверяет поддержку браузером Clipboard API для изображений
   */
  const supportsClipboardImages = useCallback(() => {
    return (
      navigator.clipboard &&
      typeof navigator.clipboard.write === 'function' &&
      typeof ClipboardItem === 'function'
    );
  }, []);

  /**
   * Создает невидимую ссылку для копирования через метод данных
   */
  const copyViaSafariMethod = (blob) => {
    // Создаем ссылку для скачивания изображения
    const url = URL.createObjectURL(blob);
    
    // Создаем временное изображение для отображения
    const tempImg = document.createElement('img');
    tempImg.onload = () => {
      try {
        // Создаем canvas на основе размеров изображения
        const canvas = document.createElement('canvas');
        canvas.width = tempImg.width;
        canvas.height = tempImg.height;
        
        // Рисуем изображение на canvas
        const ctx = canvas.getContext('2d');
        ctx.drawImage(tempImg, 0, 0);
        
        // Добавляем canvas в DOM временно
        canvas.style.position = 'fixed';
        canvas.style.top = '0';
        canvas.style.left = '0';
        canvas.style.opacity = '0';
        canvas.style.pointerEvents = 'none';
        document.body.appendChild(canvas);
        
        // Выбираем canvas
        canvas.focus();
        
        // В Safari это более надежно, чем execCommand('selectall')
        const range = document.createRange();
        range.selectNode(canvas);
        
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        
        // Пытаемся копировать
        document.execCommand('copy');
        
        // Удаляем временные элементы
        selection.removeAllRanges();
        document.body.removeChild(canvas);
        URL.revokeObjectURL(url);
      } catch (e) {
        console.error('Safari copy failed:', e);
        setCopyErrorMessage('Automatic copying not available in this browser.');
      }
    };
    
    // Загружаем изображение
    tempImg.src = url;
  };

  /**
   * Логика копирования изображения в буфер обмена
   */
  const handleCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // before‑callback
    if (typeof onBeforeAction === 'function') {
      const canContinue = await Promise.resolve(onBeforeAction());
      if (!canContinue) {
        return;
      }
    }

    setCopyErrorMessage('');
    setSuccessMessage('');

    if (!defaultSize) {
      setCopyErrorMessage("No default size available for copying.");
      return;
    }

    // Для копирования работаем только с одним ассетом
    if (!assets || assets.length === 0) {
      setCopyErrorMessage("No assets available for copying.");
      return;
    }

    if (assets.length > 1) {
      setCopyErrorMessage("Cannot copy multiple assets. Use download for multiple files.");
      return;
    }

    try {
      const result = await handleDownloadLogic(defaultSize);
      
      if (result.type !== 'single') {
        setCopyErrorMessage("Cannot copy archive files.");
        return;
      }

      const blob = result.blob;

      // Определяем браузер
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const useModernAPI = supportsClipboardImages() && !isSafari;
      
      if (useModernAPI) {
        try {
          // Современный метод (Chrome, Edge)
          await navigator.clipboard.write([
            new ClipboardItem({ 'image/png': blob }),
          ]);
          
          setSuccessMessage('Copied!');
          
          // Сообщаем родителю, что копирование УСПЕШНО
          if (onActionComplete) {
            onActionComplete();
          }
        } catch (clipboardError) {
          console.error('Modern clipboard API failed:', clipboardError);
          // Если современный API не сработал, пробуем Safari метод
          copyViaSafariMethod(blob);
          setSuccessMessage('Copied!');
          if (onActionComplete) onActionComplete();
        }
      } else {
        // Safari/Firefox метод
        copyViaSafariMethod(blob);
        setSuccessMessage('Copied!');
        
        // Сообщаем родителю, что копирование УСПЕШНО
        if (onActionComplete) {
          onActionComplete();
        }
      }
    } catch (error) {
      console.error('Copy error:', error);
      setCopyErrorMessage(`Error copying PNG: ${error.message || 'Unknown error'}`);
    }
  };

  // Упрощённая логика скачивания
  const startDownload = useCallback(async (downloadSize) => {
    if (debug) console.log('[DownloadPNGButton] startDownload with:', downloadSize);
    setIsDownloading(true);
    try {
      const result = await handleDownloadLogic(downloadSize);
      if (debug) console.log('[DownloadPNGButton] handleDownloadLogic result:', result);

      if (result.type === 'single' || result.type === 'archive') {
        saveAs(result.blob, result.fileName);

        // Сообщаем, что скачивание завершилось УСПЕШНО
        if (onActionComplete) {
          onActionComplete();
        }

        // (Если у вас была логика показа MakeaLinkPopup, можно оставить)
        if (!is_purchased && !isSubscriptionActive) {
          // ...
        }
      }
    } catch (error) {
      if (debug) console.log('[DownloadPNGButton] Error:', error);
      setErrorMessage(`Error downloading PNG: ${error.message}`);
    } finally {
      setIsDownloading(false);
      setShowDropdown(false);
    }
  }, [
    debug,
    handleDownloadLogic,
    is_purchased,
    isSubscriptionActive,
    onActionComplete,
    setErrorMessage,
    setIsDownloading
  ]);

  /**
   *  Вызывается при клике по конкретному размеру в выпадающем меню.
   *  1) Проверяем onBeforeAction
   *  2) Если false — прерываем
   *  3) Иначе стартуем скачивание
   */
  const handleDownload = useCallback(async (downloadSize) => {
    if (onBeforeAction) {
      const canContinue = await Promise.resolve(onBeforeAction());
      if (!canContinue) {
        return;
      }
    }

    startDownload(downloadSize);
  }, [onBeforeAction, startDownload]);

  // Клик по основной кнопке (defaultSize)
  const handleDefaultDownload = async (e) => {
    e.stopPropagation();
    if (!defaultSize) {
      setErrorMessage("No default size available for this collection.");
      return;
    }
    // Проверяем onBeforeAction
    if (onBeforeAction) {
      const canContinue = await Promise.resolve(onBeforeAction());
      if (!canContinue) {
        return;
      }
    }
    // Продолжаем скачивание
    handleDownload(defaultSize);
  };

  // Клик по пункту в выпадающем списке
  const handleSizeSelection = (size) => {
    if (!collectionName) {
      return;
    }
    saveSelectedSize(collectionName, size);
    handleDownload(size);
  };

  const handleToggleDropdown = (e) => {
    e.stopPropagation();
    setShowDropdown(!showDropdown);
  };

  return (
    <div className="inline-block relative w-full" ref={dropdownRef}>
      <div className="flex border border-black shadow-md rounded-md overflow-hidden min-h-[55px]">
        {/* Основная кнопка скачивания */}
        <button
          className="flex-1 px-3 py-1 bg-transparent text-black hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          disabled={isDownloading}
          onClick={handleDefaultDownload}
        >
          <span className="flex-1">
            {isDownloading
              ? 'Downloading...'
              : `Download ${defaultSize ? `${defaultSize}x${defaultSize} ` : ''}PNG`}
          </span>
          <span
            className={`cursor-pointer inline-flex items-center justify-center rounded-full w-[30px] h-[30px] select-none transition-colors duration-200 ${
              arrowHover ? 'bg-white' : 'bg-transparent'
            }`}
            onClick={handleToggleDropdown}
            onMouseEnter={() => setArrowHover(true)}
            onMouseLeave={() => setArrowHover(false)}
          >
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 12 12" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="1"
              strokeLinecap="round"
            >
              <path d="M3 4.5l3 3 3-3"/>
            </svg>
          </span>
        </button>
        
        {/* Кнопка копирования */}
        <button
          onClick={handleCopy}
          className="px-2 py-3 hover:bg-gray-100 transition-colors flex items-center justify-center min-w-[45px] border-l border-black"
          title="Copy to clipboard"
          disabled={isDownloading || !assets || assets.length !== 1}
        >
          {successMessage ? (
            // Простая галочка как перевернутый >
            <svg 
              width="20" 
              height="20" 
              viewBox="0 0 16 16" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M3 8l3 3 7-7"/>
            </svg>
          ) : (
            // Иконка копирования
            <svg 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
            >
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
          )}
        </button>
      </div>

      {showDropdown && (
        <div className="absolute top-[calc(100%+2px)] left-0 bg-white border border-gray-300 rounded-md z-[999] p-[5px] box-border min-w-full">
          {dropdownSizes.map((size) => {
            const isFree = freeSizes.includes(size);
            // Если не куплено и нет подписки, а размер не free → отключаем
            const isDisabled = !isFree && !is_purchased && !isSubscriptionActive;
            const isHovered = hoveredSize === size;

            return (
              <div
                key={size}
                onMouseEnter={() => setHoveredSize(size)}
                onMouseLeave={() => setHoveredSize(null)}
                onClick={() => {
                  if (!isDisabled) {
                    handleSizeSelection(size);
                  }
                }}
                title={
                  isDisabled
                    ? 'All formats and sizes available after purchase.'
                    : undefined
                }
                className={`px-5 py-2.5 whitespace-nowrap transition-all duration-200 ${
                  isDisabled 
                    ? 'cursor-not-allowed text-gray-400 opacity-50' 
                    : isHovered 
                      ? 'cursor-pointer text-gray-700 bg-gray-100 rounded' 
                      : 'cursor-pointer text-black bg-transparent'
                }`}
              >
                {size}px
              </div>
            );
          })}
        </div>
      )}

      {/* Сообщения об ошибках */}
      {errorMessage && (
        <p className="text-red-500 mt-[10px]">{errorMessage}</p>
      )}
      {copyErrorMessage && (
        <p className="text-red-500 mt-[10px]">{copyErrorMessage}</p>
      )}
    </div>
  );
};

DownloadPNGButton.propTypes = {
  assets: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string.isRequired,
      collection: PropTypes.string.isRequired,
      media_type: PropTypes.string,
      is_free: PropTypes.bool,
      format: PropTypes.arrayOf(PropTypes.string).isRequired,
    })
  ).isRequired,
  debug: PropTypes.bool,
  archiveName: PropTypes.string,
  is_purchased: PropTypes.bool,
  isSubscriptionActive: PropTypes.bool,
  /** Колбэк: если вернёт (или зарезолвит) false, останавливаем скачивание */
  onBeforeAction: PropTypes.func,
  /** Колбэк: вызывается после успешного скачивания */
  onActionComplete: PropTypes.func,
};

DownloadPNGButton.defaultProps = {
  debug: false,
  archiveName: '',
  is_purchased: false,
  isSubscriptionActive: false, 
  onBeforeAction: null,
  onActionComplete: null,
};

export default DownloadPNGButton;